import React from "react";
import { centerHandoff } from "@browsemate/auth-client";
import { AuthPage } from "@wushushuochanpin/identity-react/page";
import "@wushushuochanpin/identity-react/tokens.css";
import "@wushushuochanpin/identity-react/login.css";
import {
  connectExtension,
  exchangeBrowser,
  logoutBrowser,
  restoreBrowser,
} from "./browser-login";
import { browserSessionClient, extensionRequest } from "./browser-session";
import { deliverToExtension } from "./legacy-account";
import {
  beginHostedLogin,
  forgetHostedLogin,
  loginEpoch,
  readHostedLogin,
} from "./hosted-login";
import "./styles.css";

type Phase =
  "loading" | "ready" | "redirecting" | "complete" | "error" | "conflict";
export function HostedAccount() {
  const query = React.useMemo(() => new URLSearchParams(location.search), []);
  const [phase, setPhase] = React.useState<Phase>("loading");
  const [message, setMessage] = React.useState("");
  const [attempt, setAttempt] = React.useState(0);
  const [browserReady, setBrowserReady] = React.useState(false);
  const [extensionReady, setExtensionReady] = React.useState(false);
  const [logoutBusy, setLogoutBusy] = React.useState(false);
  const target = React.useRef<"web" | "extension">(
    query.get("target") === "extension" ? "extension" : "web",
  );
  const receiver = React.useRef(query.get("receiver_challenge") ?? undefined);
  const callbackHandled = React.useRef(false);
  const controller = React.useRef<AbortController>(new AbortController());
  const generation = React.useRef(0);
  const starting = React.useRef(false);

  React.useEffect(() => {
    const abort = new AbortController();
    controller.current = abort;
    const ownedGeneration = ++generation.current;
    const epoch = loginEpoch();
    const freshRequired = query.get("center") === "fresh_required";
    const current = () =>
      !abort.signal.aborted &&
      generation.current === ownedGeneration &&
      loginEpoch() === epoch;
    let completed = false;
    let webEstablished = false;
    let extensionEstablished = false;
    let reconciling = false;
    setPhase("loading");
    setMessage("");
    const invalidate = () => {
      generation.current++;
      abort.abort();
    };
    const channel = new BroadcastChannel("browsemate.browser.session");
    const loggedOut = () => {
      invalidate();
      setBrowserReady(false);
      setExtensionReady(false);
      setPhase("ready");
      setMessage("此浏览器已退出，请重新登录。");
    };
    channel.onmessage = (event) => {
      if (event.data === "logout-start") {
        invalidate();
        setPhase("loading");
      }
      if (event.data === "logout") loggedOut();
      if (event.data === "logout-failed") setAttempt((value) => value + 1);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "browsemate.login.epoch" && !current()) loggedOut();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        starting.current = false;
        setAttempt((value) => value + 1);
      }
    };
    const report = async (error: unknown) => {
      if (!current()) return;
      if (error instanceof Error && error.message === "ACCOUNT_MISMATCH") {
        try {
          setBrowserReady(!!(await browserSessionClient().status()));
        } catch {
          /* preserve conflict */
        }
        if (!current()) return;
        setMessage("网页和扩展登录的是不同账号，请退出其中一个账号后重试。");
        setPhase("conflict");
      } else if (webEstablished || extensionEstablished) {
        completed = true;
        setPhase("complete");
        setMessage(
          extensionEstablished
            ? "扩展已登录，网页会话暂未同步。请检查网络后刷新此页。"
            : "网页已登录，扩展尚未确认接收。请重新打开扩展后重试。",
        );
      } else {
        setPhase("error");
        setMessage(
          error instanceof Error && error.message === "MISSING_RECEIVER"
            ? "请从网海轻舟扩展重新发起登录。"
            : "登录未完成或已过期，请重新登录。若从扩展进入，请重新打开扩展发起登录。",
        );
      }
    };
    const reconcile = async () => {
      if (
        !current() ||
        !completed ||
        !webEstablished ||
        reconciling ||
        document.visibilityState === "hidden"
      )
        return;
      reconciling = true;
      try {
        const session = await browserSessionClient().status();
        if (!current()) return;
        if (!session) {
          completed = false;
          setBrowserReady(false);
          setExtensionReady(false);
          setPhase("ready");
          setMessage("登录已失效，请重新登录。");
          return;
        }
        const delivered = await connectExtension(
          session,
          undefined,
          current,
          abort.signal,
        );
        if (current()) {
          setExtensionReady(delivered);
          setMessage("");
        }
      } catch (error) {
        await report(error);
      } finally {
        reconciling = false;
      }
    };
    const onMessage = (event: MessageEvent) => {
      if (
        event.source === window &&
        event.origin === location.origin &&
        event.data?.source === "browsemate-extension" &&
        event.data?.type === "BROWSEMATE_SESSION_CHANGED_V1"
      )
        void reconcile();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", reconcile);
    document.addEventListener("visibilitychange", reconcile);
    window.addEventListener("message", onMessage);
    void (async () => {
      // React's setup/cleanup probe must not redeem a single-use callback.
      await Promise.resolve();
      if (!current()) return;
      try {
        if (!callbackHandled.current && query.has("center")) {
          callbackHandled.current = true;
          const transaction = query.get("transaction") ?? "";
          if (query.get("center") !== "complete") {
            try {
              const pending = readHostedLogin(transaction, null);
              target.current = pending.target;
              receiver.current = pending.challenge;
            } catch {
              /* Invalid return requires a fresh product entry. */
            }
            forgetHostedLogin(transaction);
            throw new Error("CENTER_LOGIN_FAILED");
          }
          const pending = readHostedLogin(transaction, query.get("target"));
          target.current = pending.target;
          receiver.current = pending.challenge;
          const code = await centerHandoff(
            new URL(location.origin),
            transaction,
            AbortSignal.any([abort.signal, AbortSignal.timeout(15000)]),
          );
          if (!current()) return;
          if (pending.target === "extension") {
            const delivered = await deliverToExtension(
              code,
              true,
              pending.challenge,
            );
            if (!current()) return;
            if (!delivered) throw new Error("HANDOFF_FAILED");
            extensionEstablished = true;
            setExtensionReady(true);
          } else {
            if (!pending.verifier) throw new Error("MISSING_RECEIVER");
            await exchangeBrowser(code, pending.verifier, current);
            if (!current()) return;
            webEstablished = true;
            setBrowserReady(true);
          }
          forgetHostedLogin(transaction);
          history.replaceState({}, "", "/");
        }
        const restored = await restoreBrowser(current, abort.signal);
        if (!current()) return;
        if (restored.freshReceiver) {
          target.current = "extension";
          receiver.current = restored.freshReceiver;
        }
        if (target.current === "extension" && !receiver.current)
          receiver.current = restored.receiverChallenge;
        if (restored.session && !restored.freshReceiver) {
          webEstablished = true;
          setBrowserReady(true);
          const delivered =
            restored.extensionReady ||
            (await connectExtension(
              restored.session,
              target.current === "extension" ? receiver.current : undefined,
              current,
              abort.signal,
            ));
          if (!current()) return;
          if (target.current === "extension" && !delivered)
            throw new Error("HANDOFF_FAILED");
          setExtensionReady(delivered);
          completed = true;
          setPhase("complete");
          history.replaceState({}, "", "/");
        } else if (extensionEstablished) {
          completed = true;
          setPhase("complete");
          setMessage("扩展已登录，网页会话暂未同步。请检查网络后刷新此页。");
        } else {
          if (target.current === "extension" && !receiver.current)
            throw new Error("MISSING_RECEIVER");
          setPhase("ready");
        }
      } catch (error) {
        if (freshRequired) {
          setPhase("error");
          setMessage("退出后需要重新完成微信扫码确认，请重新登录。");
          return;
        }
        await report(error);
      }
    })();
    return () => {
      invalidate();
      channel.close();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", reconcile);
      document.removeEventListener("visibilitychange", reconcile);
      window.removeEventListener("message", onMessage);
    };
  }, [attempt, query]);

  const login = async () => {
    if (starting.current) return;
    starting.current = true;
    const abort = new AbortController();
    controller.current.abort();
    controller.current = abort;
    const ownedGeneration = ++generation.current;
    const current = () =>
      generation.current === ownedGeneration && !abort.signal.aborted;
    setPhase("redirecting");
    setMessage("");
    try {
      if (target.current === "extension") {
        // Every explicit attempt needs a fresh, tab-bound receiver. A previous
        // exchange may have consumed the challenge even when its ACK was lost.
        const prepared = await extensionRequest(
          "BROWSEMATE_BROWSER_PREPARE_V1",
          {},
          abort.signal,
        );
        if (!current()) return;
        if (prepared?.ok && prepared.alreadyAuthenticated) {
          setAttempt((value) => value + 1);
          return;
        }
        if (!prepared?.ok || !prepared.receiverChallenge)
          throw new Error("MISSING_RECEIVER");
        receiver.current = prepared.receiverChallenge;
      }
      const url = await beginHostedLogin(
        target.current,
        receiver.current,
        current,
        AbortSignal.any([abort.signal, AbortSignal.timeout(15000)]),
      );
      if (current()) location.assign(url);
    } catch (error) {
      if (current()) {
        setPhase("error");
        setMessage(
          error instanceof Error && error.message === "MISSING_RECEIVER"
            ? "请从网海轻舟扩展重新发起登录。"
            : "暂时无法前往认证中心，请检查网络后重试。",
        );
      }
    } finally {
      starting.current = false;
    }
  };
  const logout = async () => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    generation.current++;
    controller.current.abort();
    const channel = new BroadcastChannel("browsemate.browser.session");
    channel.postMessage("logout-start");
    try {
      await logoutBrowser();
      channel.postMessage("logout");
      setBrowserReady(false);
      setExtensionReady(false);
      setPhase("ready");
      setMessage("已退出此浏览器，请重新登录。");
      void extensionRequest("BROWSEMATE_BROWSER_RECHECK_V1");
    } catch (error) {
      channel.postMessage("logout-failed");
      setMessage(
        error instanceof Error && error.message === "EXTENSION_UPDATE_REQUIRED"
          ? "请先在浏览器扩展管理页重新加载网海轻舟扩展，再重试退出。"
          : "退出结果尚未确认，请检查网络后重试退出。",
      );
      setPhase("complete");
    } finally {
      channel.close();
      setLogoutBusy(false);
    }
  };
  const error = phase === "error" || phase === "conflict";
  return (
    <AuthPage
      productName="BrowseMate"
      brandName="网海轻舟"
      title={phase === "complete" ? "登录成功" : "账号登录"}
      tone={error ? "error" : phase === "complete" ? "success" : "normal"}
      message={
        error
          ? message
          : phase === "complete"
            ? extensionReady
              ? "登录已安全交给网海轻舟扩展。"
              : "已登录此浏览器网页。"
            : phase === "loading"
              ? "正在检查登录状态…"
              : phase === "redirecting"
                ? "正在前往认证中心…"
                : "前往鲜知认证中心完成登录。"
      }
      footer="登录只验证身份，不会解锁或上传密码库。"
      actions={
        <>
          {(phase === "ready" || phase === "error") && (
            <button type="button" onClick={() => void login()}>
              {phase === "error" || message ? "重新登录" : "继续登录"}
            </button>
          )}
          {phase === "conflict" && (
            <button
              type="button"
              onClick={() => setAttempt((value) => value + 1)}
            >
              重新检查
            </button>
          )}
          {browserReady && (phase === "complete" || phase === "conflict") && (
            <button
              type="button"
              className="idc-login__secondary"
              disabled={logoutBusy}
              onClick={() => void logout()}
            >
              {logoutBusy
                ? "正在退出…"
                : phase === "conflict"
                  ? "退出网页账号"
                  : "退出此账户"}
            </button>
          )}
        </>
      }
    >
      {!error && message && (
        <p role={phase === "complete" ? "alert" : "status"}>{message}</p>
      )}
      {phase === "complete" && (
        <p>
          {extensionReady
            ? "请返回扩展继续使用，可以关闭此页。密码库需单独解锁。"
            : "点击浏览器工具栏中的网海轻舟图标，将自动恢复登录，无需再次扫码。"}
        </p>
      )}
    </AuthPage>
  );
}
